import {
    ButtonGhost,
    Card,
    Checkbox,
    Colors,
    Heading,
    Icon,
    PageContainer,
    Text,
} from '@amelio/ui-library';

import { EVALUATION_TYPE_ICON_NAMES } from '../evaluationTypeIcons';
import { EvaluationTypeTile } from './EvaluationTypeTile/EvaluationTypeTile';

import type { EvaluationElementsSelection } from '../evaluationSelection';
import type { EvaluationTypeValue } from '@/constants/cycle-evaluation/CycleEvaluation';
import type { OptionProps } from '@amelio/ui-library';

import { Bone, Skeleton } from '@/components/Skeleton/Skeleton';
import { EvaluationType } from '@/constants/cycle-evaluation/CycleEvaluation';
import { useAmelioTranslations } from '@/hooks';
import { useCompanyInformation } from '@/hooks/company/useCompanyInformation';
import { StepFooter } from '@/navigation/shared/StepFooter';
import {
    CYCLE_CARD_SURFACE,
    CYCLE_CARD_SURFACE_BG_COLOR,
    CYCLE_CARD_SURFACE_BORDER_COLOR,
} from '@/pages/CycleEvaluation/components/cycleCardSurface';

export interface EvaluationElementsProps {
    isLoading: boolean;
    isReadOnly?: boolean;
    unavailableTypes?: readonly EvaluationTypeValue[];
    selection: EvaluationElementsSelection;
    typesWithUnavailableTemplate: readonly EvaluationTypeValue[];
    templateOptionsByType: Record<EvaluationTypeValue, OptionProps[]>;
    onToggleType: (type: EvaluationTypeValue) => void;
    onTemplateChange: (type: EvaluationTypeValue, templateId: string) => void;
    onToggleOrganizationalValues: () => void;
    onManageTemplates: () => void;
}

export const EvaluationElements = ({
    isLoading,
    isReadOnly = false,
    unavailableTypes = [],
    selection,
    typesWithUnavailableTemplate,
    templateOptionsByType,
    onToggleType,
    onTemplateChange,
    onToggleOrganizationalValues,
    onManageTemplates,
}: EvaluationElementsProps) => {
    const { t } = useAmelioTranslations();
    const { getManagerLabel } = useCompanyInformation();

    const isTypeFrozen = (evaluationType: EvaluationTypeValue) =>
        isReadOnly || unavailableTypes.includes(evaluationType);

    const templateErrorMessageFor = (evaluationType: EvaluationTypeValue) =>
        typesWithUnavailableTemplate.includes(evaluationType)
            ? t('cycleEvaluationEvaluationElements.validation.templateNoLongerAvailable')
            : undefined;

    const noTemplateMessage = (
        <div
            className={'flex flex-wrap items-center gap-x-2 gap-y-1'}
            role={'status'}
            data-testid={'no-template-message'}
        >
            {/* Icon and sentence are one item, so the sentence wraps beside the icon instead of
                dropping under it. The button only shares their line once they have their basis. */}
            <span className={'flex min-w-0 flex-1 basis-64 items-start gap-2'}>
                <Icon
                    name={'essential-info-circle'}
                    color={Colors.BASE_400}
                    className={'shrink-0'}
                />

                <Text
                    size={'small'}
                    color={Colors.BASE_400}
                >
                    {t('cycleEvaluationEvaluationElements.template.noneAvailable')}
                </Text>
            </span>

            {!isReadOnly && (
                <ButtonGhost
                    size={'sm'}
                    onClick={onManageTemplates}
                    dataTestId={'manage-templates-link'}
                >
                    {t('cycleEvaluationEvaluationElements.template.createOne')}
                </ButtonGhost>
            )}
        </div>
    );

    return (
        <PageContainer className={'cycle-evaluation-group-section'}>
            <Card
                padding={'none'}
                className={CYCLE_CARD_SURFACE}
                bgColor={CYCLE_CARD_SURFACE_BG_COLOR}
                borderColor={CYCLE_CARD_SURFACE_BORDER_COLOR}
            >
                <Heading level={4}>{t('cycleEvaluationEvaluationElements.header.heading')}</Heading>

                <Text className={'mt-3'}>
                    {t('cycleEvaluationEvaluationElements.header.description')}
                </Text>

                {isLoading ? (
                    <Skeleton className={'mt-9 flex flex-col gap-4'}>
                        <Bone className={'h-28 w-full'} />

                        <Bone className={'h-28 w-full'} />

                        <Bone className={'h-28 w-full'} />
                    </Skeleton>
                ) : (
                    <div className={'mt-9 flex flex-col gap-4'}>
                        <EvaluationTypeTile
                            iconName={EVALUATION_TYPE_ICON_NAMES[EvaluationType.MANAGER_EVALUATION]}
                            label={t(
                                'cycleEvaluationEvaluationElements.types.managerAssessment.label',
                                { managerLabel: getManagerLabel() },
                            )}
                            description={t(
                                'cycleEvaluationEvaluationElements.types.managerAssessment.description',
                                { managerLabel: getManagerLabel() },
                            )}
                            isSelected={selection.managerAssessment}
                            isReadOnly={isTypeFrozen(EvaluationType.MANAGER_EVALUATION)}
                            templateOptions={
                                templateOptionsByType[EvaluationType.MANAGER_EVALUATION]
                            }
                            templateId={selection.templateIds[EvaluationType.MANAGER_EVALUATION]}
                            templateHint={t(
                                'cycleEvaluationEvaluationElements.template.managerHint',
                                { managerLabel: getManagerLabel() },
                            )}
                            noTemplateMessage={noTemplateMessage}
                            templateErrorMessage={templateErrorMessageFor(
                                EvaluationType.MANAGER_EVALUATION,
                            )}
                            dataTestId={'evaluation-type-manager'}
                            onToggle={() => onToggleType(EvaluationType.MANAGER_EVALUATION)}
                            onTemplateChange={(templateId) =>
                                onTemplateChange(EvaluationType.MANAGER_EVALUATION, templateId)
                            }
                        >
                            <div className={'flex flex-wrap gap-x-8 gap-y-3'}>
                                <div className={'flex items-center gap-2'}>
                                    <Checkbox
                                        checked={true}
                                        disabled={true}
                                        label={t(
                                            'cycleEvaluationEvaluationElements.subOptions.assessCompetencies',
                                        )}
                                        dataTestId={'assess-competencies-checkbox'}
                                    />

                                    <Text
                                        size={'small'}
                                        color={Colors.BASE_400}
                                    >
                                        {`(${t('cycleEvaluationEvaluationElements.subOptions.required')})`}
                                    </Text>
                                </div>

                                <Checkbox
                                    checked={selection.assessOrganizationalValues}
                                    readOnly={isReadOnly}
                                    label={t(
                                        'cycleEvaluationEvaluationElements.subOptions.assessOrganizationalValues',
                                    )}
                                    onChange={onToggleOrganizationalValues}
                                    dataTestId={'assess-organizational-values-checkbox'}
                                />
                            </div>
                        </EvaluationTypeTile>

                        <EvaluationTypeTile
                            iconName={EVALUATION_TYPE_ICON_NAMES[EvaluationType.EVALUATION_360]}
                            label={t('cycleEvaluationEvaluationElements.types.feedback360.label')}
                            description={t(
                                'cycleEvaluationEvaluationElements.types.feedback360.description',
                            )}
                            isSelected={selection.feedback360}
                            isReadOnly={isReadOnly}
                            templateOptions={templateOptionsByType[EvaluationType.EVALUATION_360]}
                            templateId={selection.templateIds[EvaluationType.EVALUATION_360]}
                            noTemplateMessage={noTemplateMessage}
                            templateErrorMessage={templateErrorMessageFor(
                                EvaluationType.EVALUATION_360,
                            )}
                            dataTestId={'evaluation-type-feedback-360'}
                            onToggle={() => onToggleType(EvaluationType.EVALUATION_360)}
                            onTemplateChange={(templateId) =>
                                onTemplateChange(EvaluationType.EVALUATION_360, templateId)
                            }
                        />

                        <EvaluationTypeTile
                            iconName={EVALUATION_TYPE_ICON_NAMES[EvaluationType.SELF_EVALUATION]}
                            label={t(
                                'cycleEvaluationEvaluationElements.types.selfAssessment.label',
                            )}
                            description={t(
                                'cycleEvaluationEvaluationElements.types.selfAssessment.description',
                            )}
                            isSelected={selection.selfAssessment}
                            isReadOnly={isTypeFrozen(EvaluationType.SELF_EVALUATION)}
                            templateOptions={templateOptionsByType[EvaluationType.SELF_EVALUATION]}
                            templateId={selection.templateIds[EvaluationType.SELF_EVALUATION]}
                            noTemplateMessage={noTemplateMessage}
                            templateErrorMessage={templateErrorMessageFor(
                                EvaluationType.SELF_EVALUATION,
                            )}
                            dataTestId={'evaluation-type-self'}
                            onToggle={() => onToggleType(EvaluationType.SELF_EVALUATION)}
                            onTemplateChange={(templateId) =>
                                onTemplateChange(EvaluationType.SELF_EVALUATION, templateId)
                            }
                        />
                    </div>
                )}

                <StepFooter />
            </Card>
        </PageContainer>
    );
};
